import {Injectable} from '@angular/core';

/**
 * Guia de foco do onboarding: o botão de continuar fica sempre habilitado e, se faltar algo, esta camada escurece a
 * tela, deixa claro só o campo que falta, aponta com um balão de seta e coloca o cursor nele. Desenhado direto no
 * body com estilo inline (fica acima do menu e não depende do styles.css global).
 */
@Injectable({providedIn: 'root'})
export class OnbFocoService {
  private camada: HTMLElement | null = null;
  private temporizador: any;
  private aoRolar = () => this.feche();

  /** `alvo`: o elemento ou o id dele. Sem alvo na tela, não faz nada (o passo segue mostrando o erro em texto). */
  mostre(alvo: HTMLElement | string | null, mensagem: string): void {
    const el = typeof alvo === 'string' ? document.getElementById(alvo) : alvo;
    if (!el) return;
    this.feche();
    el.scrollIntoView({behavior: 'smooth', block: 'center'});
    this.temporizador = setTimeout(() => this.desenhe(el, mensagem), 420);
  }

  feche(): void {
    clearTimeout(this.temporizador);
    window.removeEventListener('scroll', this.aoRolar, true);
    window.removeEventListener('resize', this.aoRolar);
    if (this.camada) this.camada.remove();
    this.camada = null;
  }

  private desenhe(el: HTMLElement, mensagem: string): void {
    const r = el.getBoundingClientRect();
    if (!r.width && !r.height) return;
    const folga = 6;
    const topo = Math.max(4, r.top - folga), esquerda = Math.max(4, r.left - folga);
    const largura = Math.min(window.innerWidth - esquerda - 4, r.width + folga * 2), altura = r.height + folga * 2;

    const camada = document.createElement('div');
    camada.setAttribute('role', 'dialog');
    camada.setAttribute('aria-live', 'polite');
    Object.assign(camada.style, {position: 'fixed', inset: '0', zIndex: '1070', cursor: 'pointer'});

    const buraco = document.createElement('div');
    Object.assign(buraco.style, {
      position: 'fixed', top: `${topo}px`, left: `${esquerda}px`, width: `${largura}px`, height: `${altura}px`,
      borderRadius: '12px', boxShadow: '0 0 0 9999px rgba(15, 17, 21, .62)', outline: '3px solid #ffffff',
      pointerEvents: 'none', transition: 'all .2s'
    });

    const embaixo = topo + altura + 110 < window.innerHeight;
    const balao = document.createElement('div');
    const largBalao = Math.min(320, window.innerWidth - 24);
    const centro = esquerda + largura / 2;
    const xBalao = Math.min(Math.max(12, centro - largBalao / 2), window.innerWidth - largBalao - 12);
    Object.assign(balao.style, {
      position: 'fixed', left: `${xBalao}px`, width: `${largBalao}px`, padding: '12px 14px', borderRadius: '12px',
      background: '#ffffff', color: '#16181b', boxShadow: '0 16px 40px rgba(0,0,0,.28)', fontFamily: "'DM Sans', -apple-system, sans-serif",
      fontSize: '14px', lineHeight: '1.45', display: 'flex', gap: '10px', alignItems: 'flex-start'
    });
    balao.style[embaixo ? 'top' : 'bottom'] = embaixo ? `${topo + altura + 14}px` : `${window.innerHeight - topo + 14}px`;

    const seta = document.createElement('div');
    Object.assign(seta.style, {
      position: 'absolute', width: '14px', height: '14px', background: '#ffffff', transform: 'rotate(45deg)',
      left: `${Math.min(Math.max(16, centro - xBalao - 7), largBalao - 30)}px`, [embaixo ? 'top' : 'bottom']: '-7px'
    });

    const icone = document.createElement('span');
    icone.textContent = embaixo ? '↑' : '↓';
    Object.assign(icone.style, {
      flex: 'none', width: '24px', height: '24px', borderRadius: '50%', background: '#2d5cb8', color: '#fff',
      display: 'grid', placeItems: 'center', fontWeight: '700', fontSize: '14px'
    });

    const texto = document.createElement('div');
    texto.textContent = mensagem;
    Object.assign(texto.style, {flex: '1'});

    balao.append(seta, icone, texto);
    camada.append(buraco, balao);
    document.body.appendChild(camada);
    this.camada = camada;

    // o anel pulsa para o olho achar o campo antes de ler
    try {
      buraco.animate([{outlineOffset: '0px', outlineColor: 'rgba(255,255,255,1)'}, {outlineOffset: '6px', outlineColor: 'rgba(255,255,255,0)'}],
        {duration: 900, iterations: 3, easing: 'ease-out'});
    } catch (e) {}

    camada.addEventListener('click', () => {
      this.feche();
      this.foque(el);
    });
    window.addEventListener('scroll', this.aoRolar, true);
    window.addEventListener('resize', this.aoRolar);
    this.foque(el);
    this.temporizador = setTimeout(() => this.feche(), 6000);
  }

  private foque(el: HTMLElement): void {
    const campo = (el.matches('input, select, textarea, button') ? el : el.querySelector('input:not([type=checkbox]), select, textarea')) as HTMLElement;
    try { campo?.focus({preventScroll: true} as any); } catch (e) {}
  }
}
